import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  Divider,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

// API hooks
import { useCreatePresentationMutation } from '../../services/api';

// Redux
import { useAppSelector } from '../../hooks/redux';
import { Question } from '../../services/slices/presentationSlice';

// Validation schema
const PresentationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  presenter: Yup.string().required('Presenter name is required'),
  description: Yup.string(),
  date: Yup.date().required('Date is required'),
});

const QuestionSchema = Yup.object().shape({
  text: Yup.string().required('Question text is required'),
  options: Yup.array()
    .of(Yup.string().required('Option cannot be empty'))
    .min(2, 'At least 2 options are required'),
});

const CreatePresentation: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    id: uuidv4(),
    text: '',
    type: 'multiple_choice',
    options: ['', ''],
    required: true,
    order: 0,
  });
  const [questionError, setQuestionError] = useState<string | null>(null);
  
  // Redux state
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.attributes?.['custom:role'] === 'admin';
  
  // Create presentation mutation
  const [createPresentation, { isLoading }] = useCreatePresentationMutation();
  
  // Formik for presentation details
  const formik = useFormik({
    initialValues: {
      title: '',
      presenter: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    },
    validationSchema: PresentationSchema,
    onSubmit: async (values) => {
      if (questions.length === 0) {
        setQuestionError('At least one question is required');
        setActiveStep(1);
        return;
      }
      
      try {
        const newPresentation = {
          ...values,
          status: 'draft' as const,
          questions,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        const result = await createPresentation(newPresentation).unwrap();
        navigate(`/presentations/${result.id}`);
      } catch (error) {
        console.error('Failed to create presentation:', error);
      }
    },
  });
  
  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 0) {
      // Validate presentation details before proceeding
      const errors: Record<string, string> = {};
      
      if (!formik.values.title) errors.title = 'Title is required';
      if (!formik.values.presenter) errors.presenter = 'Presenter is required';
      if (!formik.values.date) errors.date = 'Date is required';
      
      if (Object.keys(errors).length > 0) {
        // Trigger formik validation
        formik.validateForm();
        return;
      }
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Question handlers
  const validateQuestion = () => {
    try {
      QuestionSchema.validateSync(currentQuestion, { abortEarly: false });
      return true;
    } catch (error: any) {
      setQuestionError(error.errors?.[0] || 'Validation error');
      return false;
    }
  };
  
  const handleAddQuestion = () => {
    if (!validateQuestion()) return;
    
    const newQuestion = {
      ...currentQuestion,
      order: questions.length,
    };
    
    setQuestions([...questions, newQuestion]);
    setCurrentQuestion({
      id: uuidv4(),
      text: '',
      type: 'multiple_choice',
      options: ['', ''],
      required: true,
      order: questions.length + 1,
    });
    setQuestionError(null);
  };
  
  const handleUpdateQuestion = (index: number) => {
    if (!validateQuestion()) return;
    
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...currentQuestion,
      order: index,
    };
    
    setQuestions(updatedQuestions);
    setCurrentQuestion({
      id: uuidv4(),
      text: '',
      type: 'multiple_choice',
      options: ['', ''],
      required: true,
      order: questions.length,
    });
    setQuestionError(null);
  };
  
  const handleEditQuestion = (question: Question) => {
    setCurrentQuestion(question);
  };
  
  const handleDeleteQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    // Update order for remaining questions
    updatedQuestions.forEach((q, i) => {
      q.order = i;
    });
    setQuestions(updatedQuestions);
  };
  
  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuestion({
      ...currentQuestion,
      text: e.target.value,
    });
  };
  
  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = value;
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions,
    });
  };
  
  const handleAddOption = () => {
    setCurrentQuestion({
      ...currentQuestion,
      options: [...currentQuestion.options, ''],
    });
  };
  
  const handleRemoveOption = (index: number) => {
    if (currentQuestion.options.length <= 2) {
      setQuestionError('At least 2 options are required');
      return;
    }
    
    const updatedOptions = currentQuestion.options.filter((_, i) => i !== index);
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions,
    });
    
    setQuestionError(null);
  };
  
  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuestion({
      ...currentQuestion,
      required: e.target.checked,
    });
  };
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/presentations')}
          sx={{ mb: 2 }}
        >
          Back to Presentations
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Presentation
        </Typography>
      </Box>
      
      <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
        {/* Step 1: Presentation Details */}
        <Step>
          <StepLabel>Presentation Details</StepLabel>
          <StepContent>
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="title"
                    name="title"
                    label="Presentation Title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="presenter"
                    name="presenter"
                    label="Presenter Name"
                    value={formik.values.presenter}
                    onChange={formik.handleChange}
                    error={formik.touched.presenter && Boolean(formik.errors.presenter)}
                    helperText={formik.touched.presenter && formik.errors.presenter}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="date"
                    name="date"
                    label="Presentation Date"
                    type="date"
                    value={formik.values.date}
                    onChange={formik.handleChange}
                    error={formik.touched.date && Boolean(formik.errors.date)}
                    helperText={formik.touched.date && formik.errors.date}
                    onBlur={formik.handleBlur}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    label="Description"
                    multiline
                    rows={4}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mb: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </StepContent>
        </Step>
        
        {/* Step 2: Questions */}
        <Step>
          <StepLabel>Add Questions</StepLabel>
          <StepContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Questions ({questions.length})
              </Typography>
              
              {questionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {questionError}
                </Alert>
              )}
              
              {/* Question List */}
              {questions.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  {questions.map((question, index) => (
                    <Card key={question.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="subtitle1" gutterBottom>
                            {index + 1}. {question.text}
                          </Typography>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleEditQuestion(question)}
                            >
                              <DragIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteQuestion(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        <Box sx={{ pl: 3 }}>
                          {question.options.map((option, optIndex) => (
                            <Typography key={optIndex} variant="body2" sx={{ mb: 0.5 }}>
                              {String.fromCharCode(65 + optIndex)}. {option}
                            </Typography>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
              
              {/* Add/Edit Question Form */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {currentQuestion.id === questions.find(q => q.id === currentQuestion.id)?.id
                    ? 'Edit Question'
                    : 'Add New Question'}
                </Typography>
                
                <TextField
                  fullWidth
                  label="Question Text"
                  value={currentQuestion.text}
                  onChange={handleQuestionTextChange}
                  margin="normal"
                />
                
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Options
                </Typography>
                
                {currentQuestion.options.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`Option ${String.fromCharCode(65 + index)}`}
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      margin="dense"
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveOption(index)}
                      sx={{ ml: 1, mt: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  sx={{ mt: 1 }}
                >
                  Add Option
                </Button>
                
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentQuestion.required}
                        onChange={handleRequiredChange}
                      />
                    }
                    label="Required"
                  />
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={
                      currentQuestion.id === questions.find(q => q.id === currentQuestion.id)?.id
                        ? () => handleUpdateQuestion(questions.findIndex(q => q.id === currentQuestion.id))
                        : handleAddQuestion
                    }
                  >
                    {currentQuestion.id === questions.find(q => q.id === currentQuestion.id)?.id
                      ? 'Update Question'
                      : 'Add Question'}
                  </Button>
                </Box>
              </Paper>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{ mt: 1, mr: 1 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ mt: 1, mr: 1 }}
                disabled={questions.length === 0}
              >
                Continue
              </Button>
            </Box>
          </StepContent>
        </Step>
        
        {/* Step 3: Review & Create */}
        <Step>
          <StepLabel>Review & Create</StepLabel>
          <StepContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Presentation Details
              </Typography>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Title</Typography>
                    <Typography variant="body1">{formik.values.title}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Presenter</Typography>
                    <Typography variant="body1">{formik.values.presenter}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Date</Typography>
                    <Typography variant="body1">
                      {new Date(formik.values.date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Description</Typography>
                    <Typography variant="body1">
                      {formik.values.description || 'No description provided'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              
              <Typography variant="h6" gutterBottom>
                Questions ({questions.length})
              </Typography>
              {questions.map((question, index) => (
                <Paper key={question.id} sx={{ p: 3, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {index + 1}. {question.text}
                    {question.required && ' *'}
                  </Typography>
                  <Box sx={{ pl: 3 }}>
                    {question.options.map((option, optIndex) => (
                      <Typography key={optIndex} variant="body2" sx={{ mb: 0.5 }}>
                        {String.fromCharCode(65 + optIndex)}. {option}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{ mt: 1, mr: 1 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => formik.handleSubmit()}
                startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={isLoading}
                sx={{ mt: 1, mr: 1 }}
              >
                Create Presentation
              </Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </Container>
  );
};

export default CreatePresentation;
